'use client';

import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface SportStat {
  sport: string;
  icon: string;
  wins: number;
  losses: number;
  pushes: number;
  units: number;
  totalPicks: number;
  winPct: number;
  roi: number;
  avgOdds: number;
  record: string;
  streak: string;
  streakType: 'W' | 'L' | 'P';
}

const SPORT_ICONS: Record<string, string> = {
  NBA: '🏀', NFL: '🏈', NHL: '🏒', MLB: '⚾', NCAAB: '🏀', NCAAF: '🏈', Soccer: '⚽', UFC: '🥊',
};

export default function SportStatsTable() {
  const [sportStats, setSportStats] = useState<SportStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStats() {
      try {
        const { data, error } = await supabase
          .from('picks')
          .select('sport, result, units, units_result, odds, posted_at')
          .neq('result', 'pending');

        if (error) throw error;

        const picks = data || [];
        const bySport: Record<string, typeof picks> = {};
        picks.forEach((p) => {
          if (!bySport[p.sport]) bySport[p.sport] = [];
          bySport[p.sport].push(p);
        });

        const stats: SportStat[] = Object.entries(bySport).map(([sport, sportPicks]) => {
          const wins = sportPicks.filter((p) => p.result === 'win').length;
          const losses = sportPicks.filter((p) => p.result === 'loss').length;
          const pushes = sportPicks.filter((p) => p.result === 'push').length;
          const units = sportPicks.reduce((a, p) => a + (p.units_result || 0), 0);
          const totalRisked = sportPicks.reduce((a, p) => a + (p.units || 0), 0);
          const winPct = wins + losses > 0 ? parseFloat(((wins / (wins + losses)) * 100).toFixed(1)) : 0;
          const roi = totalRisked > 0 ? parseFloat(((units / totalRisked) * 100).toFixed(1)) : 0;
          const avgOdds = sportPicks.length > 0
            ? Math.round(sportPicks.reduce((a, p) => a + p.odds, 0) / sportPicks.length)
            : 0;

          // Calculate streak
          const sorted = [...sportPicks].sort((a, b) => new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime());
          let streakCount = 0;
          let streakType: 'W' | 'L' | 'P' = 'P';
          if (sorted.length > 0) {
            streakType = sorted[0].result === 'win' ? 'W' : sorted[0].result === 'loss' ? 'L' : 'P';
            for (const p of sorted) {
              const t = p.result === 'win' ? 'W' : p.result === 'loss' ? 'L' : 'P';
              if (t === streakType) streakCount++;
              else break;
            }
          }

          return {
            sport,
            icon: SPORT_ICONS[sport] || '🎯',
            wins, losses, pushes, units,
            totalPicks: sportPicks.length,
            winPct, roi, avgOdds,
            record: `${wins}-${losses}${pushes > 0 ? `-${pushes}` : ''}`,
            streak: `${streakType}${streakCount}`,
            streakType,
          };
        });

        setSportStats(stats.sort((a, b) => b.units - a.units));
      } catch {
        setSportStats([]);
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="mb-10">
        <h2 className="text-xl font-bold text-[hsl(var(--foreground))] mb-5">Performance by Sport</h2>
        <div className="stat-card flex items-center justify-center py-12 gap-2">
          <Loader2 size={18} className="animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading stats...</span>
        </div>
      </div>
    );
  }

  if (sportStats.length === 0) {
    return (
      <div className="mb-10">
        <h2 className="text-xl font-bold text-[hsl(var(--foreground))] mb-5">Performance by Sport</h2>
        <div className="stat-card flex items-center justify-center py-12">
          <p className="text-sm text-[hsl(var(--foreground-muted))]">No graded picks yet. Stats will appear here once picks are graded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Performance by Sport</h2>
        <span className="text-xs text-[hsl(var(--foreground-muted))]">All-time stats</span>
      </div>
      {/* Desktop table */}
      <div className="hidden lg:block stat-card overflow-hidden p-0">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                {['Sport', 'Record', 'Units', 'ROI', 'Win %', 'Avg Odds', 'Streak', 'Picks'].map((h) => (
                  <th key={h} className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sportStats.map((s, i) => (
                <tr key={s.sport} className={`border-b border-[hsl(var(--border)/0.5)] hover:bg-[hsl(var(--card-elevated))] transition-colors duration-100 ${i % 2 === 0 ? '' : 'bg-[hsl(var(--card-elevated)/0.3)]'}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2.5">
                      <span className="text-lg">{s.icon}</span>
                      <span className="font-semibold text-sm text-[hsl(var(--foreground))]">{s.sport}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4"><span className="font-mono text-sm text-[hsl(var(--foreground))] tabular-nums">{s.record}</span></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      {s.units >= 0 ? <TrendingUp size={14} className="text-[hsl(var(--primary))]" /> : <TrendingDown size={14} className="text-red-400" />}
                      <span className={`font-mono font-bold text-sm tabular-nums ${s.units >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
                        {s.units >= 0 ? '+' : ''}{s.units.toFixed(1)}u
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`font-mono text-sm font-medium tabular-nums ${s.roi >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
                      {s.roi >= 0 ? '+' : ''}{s.roi}%
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-[hsl(var(--border))]">
                        <div className="h-full rounded-full bg-[hsl(var(--primary))]" style={{ width: `${Math.min(s.winPct, 100)}%` }} />
                      </div>
                      <span className="font-mono text-sm tabular-nums text-[hsl(var(--foreground))]">{s.winPct}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-4"><span className="font-mono text-sm text-[hsl(var(--foreground))] tabular-nums">{s.avgOdds > 0 ? '+' : ''}{s.avgOdds}</span></td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-mono font-semibold ${
                      s.streakType === 'W' ? 'bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.2)]'
                      : s.streakType === 'L'? 'bg-red-500/10 text-red-400 border border-red-500/20' :'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                    }`}>
                      {s.streakType === 'W' ? <TrendingUp size={10} /> : s.streakType === 'L' ? <TrendingDown size={10} /> : <Minus size={10} />}
                      {s.streak}
                    </span>
                  </td>
                  <td className="px-5 py-4"><span className="font-mono text-sm text-[hsl(var(--foreground-muted))] tabular-nums">{s.totalPicks}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobile cards */}
      <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-3">
        {sportStats.map((s) => (
          <div key={s.sport} className="stat-card flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">{s.icon}</span>
                <span className="font-semibold text-[hsl(var(--foreground))]">{s.sport}</span>
              </div>
              <span className={`font-mono font-bold text-lg tabular-nums ${s.units >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
                {s.units >= 0 ? '+' : ''}{s.units.toFixed(1)}u
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-[hsl(var(--foreground-muted))]">Record</span>
                <span className="font-mono text-xs font-medium text-[hsl(var(--foreground))]">{s.record}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-[hsl(var(--foreground-muted))]">ROI</span>
                <span className={`font-mono text-xs font-medium ${s.roi >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>{s.roi >= 0 ? '+' : ''}{s.roi}%</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-[hsl(var(--foreground-muted))]">Win%</span>
                <span className="font-mono text-xs font-medium text-[hsl(var(--foreground))]">{s.winPct}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}